// PublicTimeSlotModal.tsx
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import { format } from 'date-fns';
import { useModal } from '../../context/ModalProvider';

/* ───────── styled bits ───────── */
const Backdrop = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: grid;
  place-items: center;
  z-index: 1000;
`;

const Sheet = styled(motion.div)`
  width: 90vw;
  max-width: 480px;
  background: #fff;
  border-radius: 16px;
  padding: 2rem 1.75rem 2.25rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const Title   = styled.h3` margin: 0; font-size: 1.25rem; `;
const Slots   = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill,minmax(110px,1fr));
  gap: .75rem;
`;
const SlotBtn = styled.button<{ $selected?: boolean }>`
  padding: .65rem .75rem;
  border-radius: 8px;
  font-size: .9rem;
  border: 1px solid
    ${({ $selected }) => ($selected ? '#6366f1' : '#d1d5db')};
  background:
    ${({ $selected }) => ($selected ? '#6366f1' : '#f9fafb')};
  color: ${({ $selected }) => ($selected ? '#fff' : '#111827')};
  cursor: pointer;
  transition: all .2s;
  &:hover { background: #eef2ff; }
`;

const Footer = styled.div`
  margin-top: .5rem;
  display: flex;
  justify-content: flex-end;
  gap: .75rem;
  button {
    padding: .7rem 1.1rem;
    border-radius: 10px;
    font-weight: 600;
    font-size: .9rem;
  }
  .closeBtn  { background:#f3f4f6; border:1px solid #e5e7eb;}
  .confirmBtn{
    background:#6366f1; color:#fff; border:none;
    &:disabled{ opacity:.5; cursor:not-allowed; }
  }
`;

/* ───────── component body ───────── */
interface Props {
  date   : Date;
  times  : string[];          // ['12:30pm','12:45pm',…] – already localised
  onSelect: (t: string) => void;
}

export default function PublicTimeSlotModal(props: Props) {
  const { close }      = useModal();
  const [sel, setSel ] = React.useState<string>();

  /* close on ESC */
  React.useEffect(() => {
    const h = (e: KeyboardEvent) => (e.key === 'Escape') && close();
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [close]);

  const handleConfirm = () => {
    if (!sel) return;
    props.onSelect(sel);
    close();
  };

  return (
    <AnimatePresence>
      <Backdrop
        key="back"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={close}
      >
        <Sheet
          key="sheet"
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0,  opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 380, damping: 28 }}
          onClick={e => e.stopPropagation()}        // keep clicks inside
        >
          <Title>
            Select a time – {format(props.date, 'MMMM d, yyyy')}
          </Title>

          <Slots>
            {props.times.map(t => (
              <SlotBtn
                key={t}
                $selected={sel === t}
                onClick={() => setSel(t)}
              >
                {t}
              </SlotBtn>
            ))}
          </Slots>

          <Footer>
            <button className="closeBtn"  onClick={close}>Cancel</button>
            <button
              className="confirmBtn"
              disabled={!sel}
              onClick={handleConfirm}
            >
              Confirm
            </button>
          </Footer>
        </Sheet>
      </Backdrop>
    </AnimatePresence>
  );
}
